// Fichier : AddTab.tsx
// Ce fichier contient la définition et l'implémentation du composant React pour ajouter une nouvelle Tab.
// Il utilise React Hook Form et Zod pour la validation de formulaire, RTK Query Pour la gestion des mutations, et React Roastify pour Notifications .

import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateTabMutation } from "@/state/usersApiSlice";
import styles from "./AddTab.module.css";
import { useNavigate } from "react-router-dom";
import { tabSchema } from "@/zod Schemas/zodTabSchema";
import { Slide, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IoReturnDownBackSharp } from "react-icons/io5";

// Définition du type de données du formulaire basé sur le schéma Zod
type TabFormData = z.infer<typeof tabSchema>;

// Fonction principale du composant AddTab
const AddTab = () => {
  const navigate = useNavigate(); // Hook pour la navigation de React Router Dom
  const [createTab, { isLoading, isError, isSuccess }] = useCreateTabMutation(); // La Mutation pour créer une nouvelle Tab

  // Hook pour gérer le formulaire avec validation Zod
  const { register, handleSubmit } = useForm<TabFormData>({
    resolver: zodResolver(tabSchema), // Utilisation de Zod pour la validation du formulaire
    defaultValues: {
      id: "",
      tab_name: "",
      link: "",
      link_type: "wordpress",
    }, // Valeurs par défaut du formulaire
  });

  // Fonction de notification pour afficher un message de succès (Un Toast)
  const notify = () =>
    toast.success("Tab Added Successfully", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      transition: Slide,
    });

  // Fonction appelée lors de la soumission du formulaire
  const onSubmit: SubmitHandler<TabFormData> = async (tab) => {
    try {
      await createTab(tab).unwrap(); // Si la création est réussie, afficher une notification (Toast) et rediriger
      notify();
      navigate("/tabs/all");
    } catch (error) {
      console.error("failed to create tab", error);
      alert("Failed to create user. Check the Console");
    }
  };

  // Retourne l'interface utilisateur du composant
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <h2 className={styles.title}>Add New Tab</h2>
            <div className={styles.inputGroup}>
              <label htmlFor="id" className={styles.label}>
                ID:
              </label>
              <input
                type="text"
                {...register("id")}
                id="id"
                name="id"
                required
                className={styles.input}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="tab_name" className={styles.label}>
                Tab Name:
              </label>
              <input
                type="text"
                id="tab_name"
                {...register("tab_name")}
                name="tab_name"
                required
                className={styles.input}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="link" className={styles.label}>
                Link:
              </label>
              <input
                type="text"
                id="link"
                {...register("link")}
                name="link"
                required
                className={styles.input}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="link_type" className={styles.label}>
                Link Type:
              </label>
              <select
                id="link_type"
                {...register("link_type")}
                name="link_type"
                required
                className={`${styles.input} ${styles.inputDisabled}`}
              >
                <option value="wordpress">WordPress</option>
                <option value="chatwoot">Chatwoot</option>
                <option value="fcm">FCM</option>
                <option value="transactions">Transactions</option>
              </select>
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="submit"
                disabled={isLoading}
                className={styles.buttonPrimary}
              >
                {isLoading ? "Adding Tab..." : "Add Tab"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/tabs")}
                className={styles.buttonSecondary}
              >
                Go back to Tabs List <IoReturnDownBackSharp />
              </button>
            </div>
            {isError && (
              <p className={styles.errorMessage}>
                There is a problem, Check the Console
              </p>
            )}
            {isSuccess && (
              <p className={styles.successMessage}>Tab Created Successfully</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTab;
